<template>
  <!--
    Tirai gelap di belakang menu ketika dibuka di ponsel.
    Tanpa ini, panel menu melayang di atas isi halaman dan pengguna kehilangan
    petunjuk bahwa halaman di belakangnya masih ada.
  -->
  <div
    v-if="terbuka"
    class="sidebar__tirai"
    @click="tutup"
    aria-hidden="true"
  ></div>

  <aside class="sidebar" :class="{ 'sidebar--terbuka': terbuka, 'sidebar--collapsed': collapsed }">
    <div class="sidebar__kepala">
      <div class="sidebar__baris-merek">
        <router-link to="/admin" class="sidebar__merek" @click="tutup">
          <BrandLogo size="sm" />
        </router-link>

        <button
          type="button"
          class="sidebar__tutup"
          aria-label="Tutup sidebar"
          title="Tutup sidebar"
          @click="tutupKlik"
        >
          <i class="bi bi-x-lg" aria-hidden="true"></i>
        </button>
      </div>

      <div class="sidebar__baris-badge">
        <span class="sidebar__badge" :class="lencana.kelas">
          <i class="bi" :class="lencana.ikon" aria-hidden="true"></i>
          {{ lencana.teks }}
        </span>
      </div>
    </div>

    <nav class="sidebar__nav" aria-label="Menu admin">
      <!--
        Menu dikelompokkan per bidang kerja.
        Sebelumnya semuanya satu deret panjang tanpa jeda, sehingga "Kontingen"
        (pendataan peserta) berdempetan dengan "Bagan Seni" (pelaksanaan
        pertandingan) padahal dipakai orang berbeda di waktu berbeda.
      -->
      <section
        v-for="kelompok in kelompokTerisi"
        :key="kelompok.kunci"
        class="sidebar__kelompok"
      >
        <button
          type="button"
          class="sidebar__judul-kelompok"
          :aria-expanded="String(terbukaKelompok(kelompok))"
          @click="toggleKelompok(kelompok.kunci)"
        >
          <span>{{ kelompok.judul }}</span>
          <span class="sidebar__jumlah">{{ kelompok.menus.length }}</span>
          <i
            class="bi sidebar__panah-kelompok"
            :class="terbukaKelompok(kelompok) ? 'bi-chevron-up' : 'bi-chevron-down'"
            aria-hidden="true"
          ></i>
        </button>

        <ul v-show="terbukaKelompok(kelompok)" class="sidebar__daftar">
          <li v-for="menu in kelompok.menus" :key="menu.id">
            <!-- Menu induk yang hanya membuka submenu -->
            <button
              v-if="menu.url === '#'"
              type="button"
              class="sidebar__tautan sidebar__tautan--induk"
              :class="{ 'sidebar__tautan--aktif': isActive(menu) }"
              :aria-expanded="String(subMenuTerbuka === menu.id)"
              :title="labelMenu(menu)"
              @click="toggleSubMenu(menu.id, menu.children)"
            >
              <i :class="ikonMenu(menu)" aria-hidden="true"></i>
              <span class="sidebar__label">{{ labelMenu(menu) }}</span>
              <i
                class="bi sidebar__panah"
                :class="subMenuTerbuka === menu.id ? 'bi-chevron-up' : 'bi-chevron-down'"
                aria-hidden="true"
              ></i>
            </button>

            <!-- Menu biasa -->
            <router-link
              v-else
              class="sidebar__tautan"
              :to="tautan(menu.url)"
              :class="{ 'sidebar__tautan--aktif': isActive(menu) }"
              :title="labelMenu(menu)"
              @click="tutup"
            >
              <i :class="ikonMenu(menu)" aria-hidden="true"></i>
              <span class="sidebar__label">{{ labelMenu(menu) }}</span>
            </router-link>

            <ul
              v-if="menu.children && subMenuTerbuka === menu.id"
              class="sidebar__submenu"
            >
              <li v-for="anak in menu.children" :key="anak.id">
                <router-link
                  class="sidebar__tautan sidebar__tautan--anak"
                  :to="tautan(anak.url)"
                  :class="{ 'sidebar__tautan--aktif': isActive(anak) }"
                  :title="labelMenu(anak)"
                  @click="tutup"
                >
                  <span class="sidebar__titik" aria-hidden="true"></span>
                  <span class="sidebar__label">{{ labelMenu(anak) }}</span>
                </router-link>
              </li>
            </ul>
          </li>
        </ul>
      </section>

      <!-- Rangka selagi menu dimuat -->
      <div v-if="memuat" class="rangka" aria-hidden="true">
        <div v-for="i in 3" :key="i" class="rangka__kelompok">
          <span class="rangka__judul"></span>
          <span v-for="j in 3" :key="j" class="rangka__baris"></span>
        </div>
      </div>

      <p v-if="!menus.length && !memuat" class="sidebar__kosong">
        Menu belum tersedia.
      </p>
    </nav>

    <router-link to="/" class="sidebar__kaki" @click="tutup">
      <i class="bi bi-box-arrow-up-right" aria-hidden="true"></i>
      <span>Lihat situs publik</span>
    </router-link>
  </aside>
</template>

<script>
import { isPanitia, isBendahara } from '@/utils/peran';
import http from '@/services/http';
import BrandLogo from '@/components/ui/BrandLogo.vue';
import { useToast } from 'vue-toastification';

/*
| Nama menu tersimpan di basis data dalam bahasa Inggris ("Tournaments",
| "Team Members", ...). Mengubahnya di basis data berisiko: kolom `url`
| dipakai untuk pencocokan rute, dan beberapa halaman lain membaca `name`.
| Jadi penerjemahan dilakukan di sini, berdasarkan nama Inggris yang dikenal.
| Menu yang tidak ada di daftar ini tetap tampil apa adanya.
*/
const TERJEMAHAN = {
  'Dashboard': 'Dasbor',
  'Tournaments': 'Turnamen',
  'Tournament': 'Turnamen',
  'Contingents': 'Kontingen',
  'Contingent': 'Kontingen',
  'Team Members': 'Atlet',
  'Members': 'Atlet',
  'Payments': 'Tagihan & Pembayaran',
  'Payment': 'Tagihan & Pembayaran',
  'Billing': 'Tagihan',
  'Classes': 'Kelas',
  'Class': 'Kelas',
  'Match Classifications': 'Klasifikasi Pertandingan',
  'Match Categories': 'Kategori Pertandingan',
  'Age Categories': 'Kategori Usia',
  'Drawing (Tanding)': 'Bagan Tanding',
  'Drawing (Seni)': 'Bagan Seni',
  'Drawing': 'Bagan',
  'Schedule': 'Jadwal',
  'Schedules': 'Jadwal',
  'Arena': 'Gelanggang',
  'Arenas': 'Gelanggang',
  'Users': 'Pengguna',
  'User': 'Pengguna',
  'Navigation': 'Navigasi',
  'Navigation Menus': 'Menu Navigasi',
  'Menus': 'Menu',
  'Sliders': 'Slider Beranda',
  'Slider': 'Slider Beranda',
  'Slider Builder': 'Slider Beranda',
  'Match Chart': 'Bagan Pertandingan',
  'Tournament Schedule': 'Jadwal Pertandingan',
  'Tournament Arena': 'Gelanggang',
  'Tournament Activity': 'Kegiatan',
  'Tournament Category': 'Kategori Pertandingan',
  'Tournament Age Category': 'Kategori Usia',
  'Contact Person': 'Narahubung',
  'Settings': 'Pengaturan',
  'Reports': 'Laporan',
  'Perkiraan Medali': 'Perkiraan Medali',
  'Medal Forecast': 'Perkiraan Medali',
  'Activities': 'Kegiatan',
  'Contact Persons': 'Narahubung',
  'Gallery': 'Galeri',
  'Documents': 'Dokumen',
};

/*
| Ikon per menu. Sebelumnya SEMUA menu memakai ikon "file-earmark" yang sama,
| sehingga bilah sisi jadi deretan ikon kembar yang tidak membantu sama sekali
| saat memindai. Ikon dicocokkan lewat kata kunci pada nama atau url.
*/
const IKON = [
  // ── Ringkasan ──
  [/dashboard|dasbor|beranda/i, 'bi-grid-1x2'],

  // ── Penyelenggaraan Turnamen (Spesifik harus di atas pola umum 'tournament') ──
  [/jadwal-kontingen|contingent-schedule/i, 'bi-calendar2-week'],
  [/tournament-age-category|age-categor|kategori-usia/i, 'bi-person-vcard'],
  [/tournament-category|kategori-pertandingan|kategori-kejuaraan/i, 'bi-bookmark-star'],
  [/tournament-activity|activit|kegiatan|agenda/i, 'bi-calendar-event'],
  [/tournament-schedule|schedule|jadwal/i, 'bi-calendar-week'],
  [/tournament-arena|arena|gelanggang/i, 'bi-geo-alt'],
  [/contact-person|contact|narahubung/i, 'bi-headset'],
  [/jenis-tarif|tarif|biaya/i, 'bi-receipt'],
  [/turnamen-saya/i, 'bi-trophy'],
  [/tournament|turnamen/i, 'bi-trophy'],

  // ── Peserta & Keuangan ──
  [/ketersediaan-kelas/i, 'bi-clipboard-check'],
  [/contingent|kontingen/i, 'bi-flag'],
  [/member|atlet|athlete/i, 'bi-people'],
  [/keuangan/i, 'bi-cash-coin'],
  [/payment|bayar|billing|tagihan|invoice/i, 'bi-credit-card-2-front'],
  [/id-card|kartu/i, 'bi-person-badge'],
  [/impor|import|excel/i, 'bi-cloud-arrow-up'],

  // ── Pertandingan & Bagan ──
  [/perkiraan-medali|medali|medal/i, 'bi-award'],
  [/classification|klasifikasi/i, 'bi-tags'],
  [/class|kelas/i, 'bi-layers'],
  [/categor/i, 'bi-collection'],
  [/seni.*(?:drawing|bagan|bracket)|(?:drawing|bagan|bracket).*seni/i, 'bi-stars'],
  [/drawing|bagan|bracket/i, 'bi-diagram-2'],
  [/rekap|hasil/i, 'bi-clipboard-data'],

  // ── Pengaturan & Sistem ──
  [/role|permission/i, 'bi-shield-lock'],
  [/user|pengguna|account/i, 'bi-person-gear'],
  [/navigation|menu/i, 'bi-layout-text-sidebar-reverse'],
  [/setting|pengaturan/i, 'bi-sliders'],
  [/report|laporan/i, 'bi-bar-chart-line'],
  [/gallery|galeri|image|foto/i, 'bi-images'],
  [/slider/i, 'bi-window-stack'],
  [/document|dokumen/i, 'bi-file-earmark-text'],
];

/*
| Pengelompokan menu.
|
| Urutannya mengikuti alur kerja panitia dari awal sampai hari-H:
|   siapkan turnamen -> terima peserta -> susun pertandingan -> atur tampilan.
|
| Pencocokan memakai `url`, bukan `name`, karena nama menu bisa diubah panitia
| lewat halaman Navigasi sedangkan url terikat pada rute dan tidak berubah.
| Pola diperiksa berurutan dan yang pertama cocok yang dipakai, jadi pola yang
| lebih khusus (mis. 'tournament-schedule') harus berada di atas pola yang
| lebih longgar (mis. 'tournament').
*/
const KUNCI_SIMPAN = 'arspo:sidebar-kelompok-tertutup';

// ── KELOMPOK MENU ADMIN / PANITIA ──
const KELOMPOK_ADMIN = [
  {
    kunci: 'ringkasan',
    judul: 'Ringkasan Panitia',
    pola: [/^$/, /^#$/, /dashboard|dasbor|beranda/i],
  },
  {
    kunci: 'turnamen',
    judul: 'Penyelenggaraan',
    pola: [
      /jenis-tarif/i,
      /tournament-activity/i,
      /tournament-age-category/i,
      /tournament-category/i,
      /tournament-arena/i,
      /contact-person/i,
      /^tournament(?!-schedule)/i,
    ],
  },
  {
    kunci: 'peserta',
    judul: 'Peserta & Keuangan',
    pola: [/contingent|kontingen/i, /team-members|atlet|athlete/i, /payment|billing|bayar|keuangan/i, /id-card|kartu/i, /ketersediaan-kelas/i],
  },
  {
    kunci: 'pertandingan',
    judul: 'Pertandingan',
    pola: [
      /perkiraan-medali|medali/i,
      /tournament-schedule|schedule|jadwal/i,
      /match-clasification|match-classification/i,
      /match-chart|bagan/i,
      /classes|kelas/i,
      /tanding/i,
      /seni/i,
    ],
  },
  {
    kunci: 'tampilan',
    judul: 'Tampilan & Sistem',
    pola: [/slider/i, /navigation|navigasi|menu/i, /gallery|galeri/i, /page|halaman/i, /user|pengguna|role|permission|setting|pengaturan/i],
  },
];

// ── KELOMPOK MENU BENDAHARA ──
const KELOMPOK_BENDAHARA = [
  { kunci: 'keuangan_bdh', judul: 'Keuangan', pola: [/keuangan/i, /payment/i] },
];

// ── KELOMPOK MENU OFFICIAL / KONTINGEN ──
const KELOMPOK_OFFICIAL = [
  {
    kunci: 'ringkasan_off',
    judul: 'Dasbor Official',
    pola: [/^$/, /^#$/, /turnamen-saya/i, /dashboard|dasbor|beranda/i],
  },
  {
    kunci: 'atlet_off',
    judul: 'Kontingen & Atlet',
    pola: [/team-members/i, /atlet/i, /contingent|kontingen/i],
  },
  {
    kunci: 'jadwal_off',
    judul: 'Jadwal Pertandingan',
    pola: [/jadwal-kontingen/i, /contingent-schedule/i],
  },
  {
    kunci: 'keuangan_off',
    judul: 'Tagihan & Pembayaran',
    pola: [/payment/i, /billing/i, /bayar/i, /ketersediaan-kelas/i],
  },
];

export default {
  name: 'AdminSidebar',

  components: { BrandLogo },

  props: {
    /** Dikendalikan AdminLayout; hanya berpengaruh di layar sempit. */
    terbuka: { type: Boolean, default: false },
    /** Status tertutup di layar lebar (desktop). */
    collapsed: { type: Boolean, default: false },
  },

  emits: ['tutup'],

  setup() {
    return { toast: useToast() };
  },

  data() {
    return {
      subMenuTerbuka: null,
      menus: [],
      memuat: false,
      kelompokTertutup: null,
      kuotaKelasAktif: false,
    };
  },

  computed: {
    isPanitia() {
      return isPanitia();
    },

    isBendahara() {
      return isBendahara();
    },

    lencana() {
      if (this.isBendahara) return { teks: 'BENDAHARA', ikon: 'bi-cash-coin', kelas: 'sidebar__badge--admin' };
      if (this.isPanitia) return { teks: 'PANITIA / ADMIN', ikon: 'bi-shield-check', kelas: 'sidebar__badge--admin' };
      return { teks: 'OFFICIAL KONTINGEN', ikon: 'bi-person-badge', kelas: 'sidebar__badge--official' };
    },

    /**
     * Menu yang sudah dibagi ke kelompoknya berdasarkan peran pengguna
     */
    kelompokTerisi() {
      if (this.isBendahara) {
        return KELOMPOK_BENDAHARA.map((k) => ({ ...k, menus: this.menus }));
      }

      const daftarKelompok = this.isPanitia ? KELOMPOK_ADMIN : KELOMPOK_OFFICIAL;
      const wadah = new Map(daftarKelompok.map((k) => [k.kunci, { ...k, menus: [] }]));
      const lainnya = [];

      for (const menu of this.menus) {
        const url = (menu.url || '').replace(/^\/+|\/+$/g, '');
        const nama = (menu.name || '').toLowerCase();

        // Khusus Official: Jangan tampilkan menu pertandingan, hasil, bagan, perkiraan medali, jadwal, maupun kartu peserta
        // KECUALI jadwal-kontingen yang memang ditujukan untuk Official
        if (!this.isPanitia) {
          if (/jadwal-kontingen/i.test(url) || /jadwal kontingen/i.test(nama)) {
            // Izinkan lolos ke kelompok Official
          } else {
            const polaTerlarang = /schedule|jadwal|drawing|bagan|match|pertandingan|hasil|seni|tanding|perkiraan-medali|medali|arena|gelanggang|bracket|classification|tarif|jenis-tarif|id-card|kartu/i;
            if (polaTerlarang.test(url) || polaTerlarang.test(nama)) {
              continue;
            }
          }

          // Sembunyikan menu ketersediaan kelas jika dinonaktifkan panitia
          if (/ketersediaan-kelas/i.test(url) || /ketersediaan/i.test(nama)) {
            if (!this.kuotaKelasAktif) {
              continue;
            }
          }
        }

        const cocok = daftarKelompok.find((k) => k.pola.some((p) => p.test(url)));

        if (cocok) {
          wadah.get(cocok.kunci).menus.push(menu);
        } else if (this.isPanitia) {
          lainnya.push(menu);
        }
      }

      const hasil = [...wadah.values()].filter((k) => k.menus.length);

      if (this.isPanitia && lainnya.length) {
        hasil.push({ kunci: 'lainnya', judul: 'Lainnya', menus: lainnya });
      }

      return hasil;
    },
  },

  watch: {
    // Berpindah halaman lewat menu harus menutup panel di ponsel, kalau tidak
    // halaman barunya tertutup panel yang masih terbuka.
    '$route.path'() {
      this.tutup();
    },
  },

  mounted() {
    this.bacaKelompokTersimpan();
    this.ambilMenu();
  },

  methods: {
    /**
     * Menyusun alamat menu, apa pun bentuk url yang tersimpan.
     *
     * Menu lama menyimpan url TANPA awalan panel ("team-members"), dan
     * bilah sisi menambahkan "/admin/" sendiri. Menu yang ditambahkan
     * belakangan sempat disimpan LENGKAP dengan awalannya ("admin/id-card"),
     * sehingga hasilnya "/admin/admin/id-card" — tidak cocok dengan rute
     * mana pun, dan yang muncul halaman kosong tanpa pesan apa pun.
     *
     * Datanya sudah dibetulkan, tapi normalisasi ini tetap ada: url menu
     * bisa diketik panitia lewat halaman Navigasi, dan menuliskannya dengan
     * "admin/" di depan adalah tebakan yang sangat wajar.
     */
    tautan(url) {
      const bersih = String(url || '').replace(/^\/+/, '');

      if (bersih === '' || bersih === '#') return '/admin';

      return bersih.startsWith('admin/') || bersih === 'admin'
        ? '/' + bersih
        : '/admin/' + bersih;
    },

    tutupKlik() {
      // Tombol X ditekan langsung oleh pengguna
      this.$emit('tutup');
    },

    tutup() {
      // Dipanggil saat klik tautan atau perpindahan rute: hanya tutup jika di drawer layar sempit
      if (this.terbuka) this.$emit('tutup');
    },

    /**
     * Kelompok yang memuat halaman aktif SELALU terbuka, walaupun pengguna
     * pernah menutupnya. Kalau tidak, halaman yang sedang dibuka bisa
     * tersembunyi di balik kelompok tertutup dan penanda "kamu di sini"
     * menjadi tidak berguna.
     */
    terbukaKelompok(kelompok) {
      if (kelompok.menus.some((m) => this.isActive(m))) return true;
      if (!this.kelompokTertutup) return true;
      return !this.kelompokTertutup.includes(kelompok.kunci);
    },

    toggleKelompok(kunci) {
      const tertutup = new Set(this.kelompokTertutup || []);
      if (tertutup.has(kunci)) tertutup.delete(kunci);
      else tertutup.add(kunci);

      this.kelompokTertutup = [...tertutup];

      try {
        localStorage.setItem(KUNCI_SIMPAN, JSON.stringify(this.kelompokTertutup));
      } catch (e) {
        // Mode penyamaran memblokir localStorage. Pilihan tetap berlaku untuk
        // sesi ini, hanya tidak diingat setelah tab ditutup.
      }
    },

    bacaKelompokTersimpan() {
      try {
        const mentah = localStorage.getItem(KUNCI_SIMPAN);
        const nilai = mentah ? JSON.parse(mentah) : null;
        this.kelompokTertutup = Array.isArray(nilai) ? nilai : null;
      } catch (e) {
        this.kelompokTertutup = null;
      }
    },

    toggleSubMenu(id, anak) {
      if (anak && anak.length) {
        this.subMenuTerbuka = this.subMenuTerbuka === id ? null : id;
      }
    },

    /**
     * Apakah menu ini yang sedang dibuka.
     *
     * Dulu memakai path.includes(url) yang terlalu longgar:
     *   - url kosong -> ''.includes cocok dengan APA PUN, semua menu menyala;
     *   - url 'seni' cocok dengan '/admin/tournament-schedule/seni/create'
     *     sekaligus dengan '/admin/seni', dua menu berbeda menyala bersamaan.
     * Sekarang dibandingkan sebagai segmen jalur yang utuh.
     */
    isActive(item) {
      if (item.children && item.children.length) {
        return item.children.some((anak) => this.isActive(anak));
      }

      const url = (item.url || '').replace(/^\/+|\/+$/g, '');
      if (url === '#') return false;

      const jalur = this.$route.path.replace(/\/+$/, '');

      // Menu dasbor: url kosong, hanya cocok dengan /admin persis.
      if (!url) return jalur === '/admin';

      // Alamat disusun lewat tautan() yang sama dengan yang dipakai
      // router-link. Kalau keduanya menyusunnya sendiri-sendiri, menu yang
      // urlnya sudah berawalan "admin/" akan tertaut benar tapi tidak
      // pernah ditandai aktif.
      const target = this.tautan(url).replace(/\/+$/, '');
      return jalur === target || jalur.startsWith(`${target}/`);
    },

    labelMenu(menu) {
      return TERJEMAHAN[(menu.name || '').trim()] || menu.name || '';
    },

    ikonMenu(menu) {
      const teks = `${menu.name || ''} ${menu.url || ''}`;
      const cocok = IKON.find(([pola]) => pola.test(teks));
      return `bi ${cocok ? cocok[1] : 'bi-circle'} sidebar__ikon`;
    },

    async ambilMenu() {
      this.memuat = true;
      try {
        const [resMenu, resTournaments] = await Promise.all([
          http.get('/menus'),
          !this.isPanitia && !this.isBendahara ? http.get('/tournaments', { params: { fetch_all: true } }).catch(() => null) : null,
        ]);

        this.menus = resMenu?.data || [];

        // Menu bendahara sudah lengkap dari server; jangan disisipi menu jadwal.
        if (this.isBendahara) return;

        // Selalu sertakan menu Jadwal Kontingen untuk Official & Panitia
        const adaJadwal = this.menus.some((m) => (m.url || '').includes('jadwal-kontingen'));
        if (!adaJadwal) {
          this.menus.push({
            id: 'menu-jadwal-kontingen',
            name: 'Jadwal Kontingen',
            url: '/admin/jadwal-kontingen',
            icon: 'bi-calendar2-week',
          });
        }

        if (resTournaments?.data) {
          const list = Array.isArray(resTournaments.data) ? resTournaments.data : resTournaments.data?.data || [];
          this.kuotaKelasAktif = list.some((t) => Boolean(t.tampilkan_kuota_kelas));
        } else {
          this.kuotaKelasAktif = false;
        }

        // Submenu yang memuat halaman aktif dibuka otomatis, supaya posisi
        // pengguna di dalam struktur menu selalu terlihat setelah muat ulang.
        const induk = this.menus.find((m) => m.children?.length && this.isActive(m));
        if (induk) this.subMenuTerbuka = induk.id;
      } catch (e) {
        this.menus = [];
      } finally {
        this.memuat = false;
      }
    },
  },
};
</script>

<style scoped>
/*
 * Bilah sisi admin.
 * Sebelumnya dikunci ke biru dongker #1E2A57 dengan lebar tetap 250px dan
 * TANPA cara apa pun untuk menutupnya. Di layar 360px, panel selebar 250px
 * menyisakan 110px untuk isi halaman — praktis tidak bisa dipakai.
 */
.sidebar {
  position: fixed;
  inset-block: 0;
  left: 0;
  z-index: 1040;
  display: flex;
  flex-direction: column;
  width: 280px;
  max-width: 85vw;
  background: var(--bg-surface);
  border-right: 1px solid var(--border-subtle);

  /* Di ponsel panel disembunyikan ke kiri sampai dipanggil. */
  transform: translateX(-100%);
  transition: transform var(--transition-base) var(--ease-out-quart);
}

.sidebar--terbuka { transform: translateX(0); box-shadow: var(--shadow-lg); }

.sidebar__tirai {
  position: fixed;
  inset: 0;
  z-index: 1035;
  background: rgb(0 0 0 / 0.5);
  backdrop-filter: blur(2px);
}

/* ── Kepala ── */
.sidebar__kepala {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 14px 12px 14px;
  border-bottom: 1px solid var(--border-subtle);
  flex-shrink: 0;
}

.sidebar__baris-merek {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.sidebar__merek {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
}

.sidebar__baris-badge {
  display: flex;
  align-items: center;
}

.sidebar__badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  padding: 2px 8px;
  border-radius: var(--radius-full);
  width: fit-content;
}

.sidebar__badge--admin {
  background: rgba(240, 80, 8, 0.08);
  color: var(--accent);
  border: 1px solid rgba(240, 80, 8, 0.2);
}

.sidebar__badge--official {
  background: rgba(22, 163, 74, 0.08);
  color: var(--success-600);
  border: 1px solid rgba(22, 163, 74, 0.2);
}

.sidebar__tutup {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  min-width: 38px;
  min-height: 38px;
  padding: 0;
  font-size: 0.95rem;
  color: var(--text-muted);
  background: var(--bg-sunken);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  cursor: pointer;
  flex-shrink: 0;
  transition: all var(--transition-fast);
}

.sidebar__tutup:hover {
  background: var(--bg-hover);
  color: var(--danger-600, #dc2626);
  border-color: rgba(220, 38, 38, 0.35);
}

.sidebar__tutup :deep(i.bi),
.sidebar__tutup i.bi {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.sidebar__tutup :deep(i.bi::before),
.sidebar__tutup i.bi::before {
  vertical-align: 0 !important;
  line-height: 1 !important;
  margin: 0 !important;
}

/* ── Daftar menu ── */
.sidebar__nav {
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: var(--space-3) 8px var(--space-6);
}

.sidebar__nav::-webkit-scrollbar { width: 4px; }
.sidebar__nav::-webkit-scrollbar-thumb {
  background: var(--border-subtle);
  border-radius: var(--radius-full);
}

.sidebar__daftar,
.sidebar__submenu { list-style: none; margin: 0; padding: 0; }

/* ── Kelompok ── */
.sidebar__kelompok + .sidebar__kelompok {
  margin-top: var(--space-3);
  padding-top: var(--space-2);
  border-top: 1px solid var(--border-subtle);
}

.sidebar__judul-kelompok {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  width: 100%;
  min-height: 32px;
  margin: 0 0 2px;
  padding: 4px var(--space-2);
  font-family: var(--font-sans);
  font-size: 0.68rem;
  font-weight: 700;
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  background: transparent;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: color var(--transition-fast), background-color var(--transition-fast);
}

.sidebar__judul-kelompok:hover { color: var(--text-primary); background: var(--bg-hover); }

.sidebar__judul-kelompok > span:first-child {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar__jumlah {
  display: grid;
  place-items: center;
  min-width: 18px;
  height: 18px;
  padding-inline: 5px;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0;
  color: var(--text-muted);
  background: var(--bg-sunken);
  border-radius: var(--radius-full);
}

.sidebar__panah-kelompok { font-size: 0.6rem; opacity: 0.6; }

.sidebar__tautan {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  width: 100%;
  min-height: 38px;
  padding: 0.4rem 0.55rem;
  margin-bottom: 2px;
  font-family: var(--font-sans);
  font-size: 0.835rem;
  font-weight: 500;
  text-align: left;
  color: var(--text-secondary);
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  cursor: pointer;
  text-decoration: none;
  transition: all var(--transition-fast);
}

.sidebar__tautan:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.sidebar__tautan--aktif {
  color: var(--text-primary) !important;
  background: var(--bg-surface);
  border-color: var(--border-subtle);
  font-weight: 600;
  box-shadow: var(--shadow-sm);
}

.sidebar__ikon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  font-size: 0.95rem;
  border-radius: var(--radius-sm, 6px);
  color: var(--text-muted);
  background: transparent;
  flex-shrink: 0;
  transition: all var(--transition-fast);
}

.sidebar__tautan:hover .sidebar__ikon {
  color: var(--text-primary);
  background: var(--bg-sunken);
}

.sidebar__tautan--aktif .sidebar__ikon {
  color: var(--accent);
  background: var(--accent-subtle);
}

.sidebar__label {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sidebar__panah { font-size: 0.7rem; opacity: 0.6; }

.sidebar__submenu { margin: 2px 0 var(--space-2) var(--space-3); }

.sidebar__tautan--anak {
  min-height: 34px;
  font-size: var(--text-xs);
  font-weight: 500;
  padding-left: 1.25rem;
  border-radius: var(--radius-md);
}

.sidebar__tautan--anak.sidebar__tautan--aktif {
  background: var(--bg-hover);
  color: var(--accent) !important;
  box-shadow: none;
}

.sidebar__titik {
  width: 5px; height: 5px;
  margin-right: 4px;
  background: var(--border-strong);
  border-radius: 50%;
  flex-shrink: 0;
}

.sidebar__tautan--anak.sidebar__tautan--aktif .sidebar__titik { background: var(--accent); }

.rangka { padding: var(--space-2) 0; }
.rangka__kelompok { margin-bottom: var(--space-5); }

.rangka__judul,
.rangka__baris {
  display: block;
  height: 12px;
  margin: 0 var(--space-3) var(--space-2);
  background: var(--bg-hover);
  border-radius: var(--radius-sm);
  animation: kedip 1.4s ease-in-out infinite;
}

.rangka__judul { width: 45%; height: 9px; margin-bottom: var(--space-3); }
.rangka__baris { height: 16px; }
.rangka__baris:nth-child(2) { width: 78%; }
.rangka__baris:nth-child(3) { width: 62%; }
.rangka__baris:nth-child(4) { width: 85%; }

@keyframes kedip {
  0%, 100% { opacity: 0.45; }
  50%      { opacity: 0.9; }
}

@media (prefers-reduced-motion: reduce) {
  .rangka__judul, .rangka__baris { animation: none; }
}

.sidebar__kosong {
  padding: var(--space-4);
  font-size: var(--text-xs);
  color: var(--text-muted);
  text-align: center;
}

/* ── Kaki & Aksi Bawah ── */
.sidebar__kaki-wadah {
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--border-subtle);
  flex-shrink: 0;
  background: var(--bg-surface);
}

.sidebar__kaki {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: 0.85rem 1.15rem;
  font-family: var(--font-sans);
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--text-secondary);
  text-decoration: none;
  background: transparent;
  border: none;
  width: 100%;
  cursor: pointer;
  text-align: left;
  transition: all var(--transition-fast);
}

.sidebar__kaki:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.sidebar__kaki--keluar {
  color: #ef4444 !important;
  border-top: 1px solid var(--border-subtle);
  font-weight: 700;
}

.sidebar__kaki--keluar:hover {
  background: rgba(239, 68, 68, 0.12) !important;
  color: #dc2626 !important;
}

.sidebar__kaki--keluar:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── Layar lebar: panel bisa ditutup / dibuka ── */
@media (min-width: 992px) {
  .sidebar {
    transform: translateX(0);
    box-shadow: none;
  }

  .sidebar.sidebar--collapsed {
    transform: translateX(-100%) !important;
  }

  .sidebar__tirai {
    display: none !important;
  }

  .sidebar__tutup {
    display: none !important;
  }
}

@media (hover: none) and (pointer: coarse), (max-width: 768px) {
  .sidebar__tautan {
    min-height: 44px;
    padding: 0.5rem 0.75rem;
    font-size: 0.88rem;
  }
  .sidebar__tautan--anak {
    min-height: 40px;
    font-size: 0.825rem;
  }
  .sidebar__judul-kelompok {
    min-height: 38px;
    padding: 6px var(--space-2);
  }
}

@media (prefers-reduced-motion: reduce) {
  .sidebar { transition: none; }
}

.sidebar__event-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  padding: 4px 8px;
  margin-top: 6px;
}
.sidebar__event-text {
  font-size: 0.72rem;
  font-weight: 600;
  color: #f8fafc;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sidebar__event-reset {
  background: none;
  border: none;
  color: #94a3b8;
  padding: 0;
  cursor: pointer;
  line-height: 1;
  font-size: 0.85rem;
}
.sidebar__event-reset:hover {
  color: #f87171;
}
</style>
